import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  Card, 
  CardContent,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip
} from '@material-ui/core';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { useTrip } from '../../contexts/TripContext';
import { useBooking } from '../../contexts/BookingContext';
import { useVehicle } from '../../contexts/VehicleContext';
import { useCompany } from '../../contexts/CompanyContext';

// Colors for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

// Status color mapping
const STATUS_COLORS = {
  scheduled: 'primary',
  completed: 'success',
  cancelled: 'error',
  delayed: 'warning',
  inProgress: 'info'
};

const TripsDashboard = () => {
  const { getTrips, searchTrips } = useTrip();
  const { getBookings } = useBooking();
  const { getVehicles } = useVehicle();
  const { getCompanies } = useCompany();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('month');
  const [companyFilter, setCompanyFilter] = useState('all');
  
  // Dashboard data
  const [trips, setTrips] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState({
    totalTrips: 0,
    scheduledTrips: 0,
    completedTrips: 0,
    cancelledTrips: 0,
    averageOccupancy: 0,
    totalRevenue: 0
  });
  
  // Chart data
  const [tripsByStatusData, setTripsByStatusData] = useState([]);
  const [tripsByRouteData, setTripsByRouteData] = useState([]);
  const [occupancyRateData, setOccupancyRateData] = useState([]);
  const [revenueByRouteData, setRevenueByRouteData] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  useEffect(() => {
    const fetchTripsDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all required data
        const allTrips = await getTrips();
        const allBookings = await getBookings();
        const allCompanies = await getCompanies();
        
        setCompanies(allCompanies);
        
        // Filter trips based on company filter
        let filteredTrips = allTrips;
        if (companyFilter !== 'all') {
          filteredTrips = allTrips.filter(trip => trip.companyId === companyFilter);
        }
        
        // Filter trips based on time range
        const now = new Date();
        let startDate;
        
        switch (timeRange) {
          case 'week':
            startDate = new Date(now);
            startDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            startDate = new Date(now);
            startDate.setMonth(now.getMonth() - 1);
            break;
          case 'year':
            startDate = new Date(now);
            startDate.setFullYear(now.getFullYear() - 1);
            break;
          default:
            startDate = new Date(now);
            startDate.setMonth(now.getMonth() - 1);
        }
        
        filteredTrips = filteredTrips.filter(trip => {
          if (!trip.departureTime) return false;
          
          let tripDate;
          if (trip.departureTime.seconds) {
            // Firestore Timestamp
            tripDate = new Date(trip.departureTime.seconds * 1000);
          } else if (trip.departureTime instanceof Date) {
            // JavaScript Date
            tripDate = trip.departureTime;
          } else if (typeof trip.departureTime === 'string') {
            // ISO String
            tripDate = new Date(trip.departureTime);
          } else {
            return false;
          }
          
          return tripDate >= startDate;
        });
        
        setTrips(filteredTrips);
        
        // Calculate basic stats
        const totalTrips = filteredTrips.length;
        const scheduledTrips = filteredTrips.filter(trip => trip.status === 'scheduled').length;
        const completedTrips = filteredTrips.filter(trip => trip.status === 'completed').length;
        const cancelledTrips = filteredTrips.filter(trip => trip.status === 'cancelled').length;
        
        // Calculate average occupancy
        let totalOccupancy = 0;
        let tripsWithOccupancy = 0;
        
        filteredTrips.forEach(trip => {
          if (trip.totalSeats && trip.availableSeats !== undefined) {
            const occupancy = ((trip.totalSeats - trip.availableSeats) / trip.totalSeats) * 100;
            totalOccupancy += occupancy;
            tripsWithOccupancy++;
          }
        });
        
        const averageOccupancy = tripsWithOccupancy > 0 ? totalOccupancy / tripsWithOccupancy : 0;
        
        // Calculate total revenue from bookings
        let totalRevenue = 0;
        
        allBookings.forEach(booking => {
          if (booking.status !== 'cancelled' && booking.paymentStatus === 'completed') {
            const tripId = booking.tripId;
            const trip = filteredTrips.find(t => t.id === tripId);
            if (trip) {
              totalRevenue += booking.amount || 0;
            }
          }
        });
        
        setStats({
          totalTrips,
          scheduledTrips,
          completedTrips,
          cancelledTrips,
          averageOccupancy,
          totalRevenue
        });
        
        // Prepare trips by status data for chart
        const tripsByStatus = [
          { name: 'Scheduled', value: scheduledTrips },
          { name: 'Completed', value: completedTrips },
          { name: 'Cancelled', value: cancelledTrips },
          { name: 'Delayed', value: filteredTrips.filter(trip => trip.status === 'delayed').length },
          { name: 'In Progress', value: filteredTrips.filter(trip => trip.status === 'inProgress').length }
        ];
        setTripsByStatusData(tripsByStatus);
        
        // Prepare trips by route data
        const routeCounts = {};
        filteredTrips.forEach(trip => {
          const route = `${trip.origin} - ${trip.destination}`;
          if (!routeCounts[route]) {
            routeCounts[route] = 0;
          }
          routeCounts[route]++;
        });
        
        const tripsByRoute = Object.keys(routeCounts).map(route => ({
          name: route,
          value: routeCounts[route]
        })).sort((a, b) => b.value - a.value).slice(0, 10); // Top 10 routes
        
        setTripsByRouteData(tripsByRoute);
        
        // Prepare occupancy rate data
        const occupancyByRoute = {};
        const tripsCountByRoute = {};
        
        filteredTrips.forEach(trip => {
          if (trip.totalSeats && trip.availableSeats !== undefined) {
            const route = `${trip.origin} - ${trip.destination}`;
            const occupancy = ((trip.totalSeats - trip.availableSeats) / trip.totalSeats) * 100;
            
            if (!occupancyByRoute[route]) {
              occupancyByRoute[route] = 0;
              tripsCountByRoute[route] = 0;
            }
            
            occupancyByRoute[route] += occupancy;
            tripsCountByRoute[route]++;
          }
        });
        
        const occupancyRateByRoute = Object.keys(occupancyByRoute).map(route => ({
          name: route,
          value: tripsCountByRoute[route] > 0 ? occupancyByRoute[route] / tripsCountByRoute[route] : 0
        })).sort((a, b) => b.value - a.value).slice(0, 10); // Top 10 routes by occupancy
        
        setOccupancyRateData(occupancyRateByRoute);
        
        // Prepare revenue by route data
        const revenueByRoute = {};
        
        allBookings.forEach(booking => {
          if (booking.status !== 'cancelled' && booking.paymentStatus === 'completed') {
            const tripId = booking.tripId;
            const trip = filteredTrips.find(t => t.id === tripId);
            if (trip) {
              const route = `${trip.origin} - ${trip.destination}`;
              if (!revenueByRoute[route]) {
                revenueByRoute[route] = 0;
              }
              revenueByRoute[route] += booking.amount || 0;
            }
          }
        });
        
        const revenueByRouteArray = Object.keys(revenueByRoute).map(route => ({
          name: route,
          value: revenueByRoute[route]
        })).sort((a, b) => b.value - a.value).slice(0, 10); // Top 10 routes by revenue
        
        setRevenueByRouteData(revenueByRouteArray);
        
      } catch (error) {
        console.error('Error fetching trips dashboard data:', error);
        setError('Failed to load trips dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTripsDashboardData();
  }, [timeRange, companyFilter]);
  
  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };
  
  const handleCompanyFilterChange = (event) => {
    setCompanyFilter(event.target.value);
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography color="error" variant="h6">{error}</Typography>
      </Box>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Trips Dashboard
      </Typography>
      
      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            onChange={handleTimeRangeChange}
            label="Time Range"
          >
            <MenuItem value="week">Last Week</MenuItem>
            <MenuItem value="month">Last Month</MenuItem>
            <MenuItem value="year">Last Year</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Company</InputLabel>
          <Select
            value={companyFilter}
            onChange={handleCompanyFilterChange}
            label="Company"
          >
            <MenuItem value="all">All Companies</MenuItem>
            {companies.map(company => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Trips
              </Typography>
              <Typography variant="h4">
                {stats.totalTrips}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Scheduled
              </Typography>
              <Typography variant="h4">
                {stats.scheduledTrips}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed
              </Typography>
              <Typography variant="h4">
                {stats.completedTrips}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Cancelled
              </Typography>
              <Typography variant="h4">
                {stats.cancelledTrips}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg. Occupancy
              </Typography>
              <Typography variant="h4">
                {stats.averageOccupancy.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Revenue
              </Typography>
              <Typography variant="h4">
                ${stats.totalRevenue.toFixed(0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Charts */}
      <Grid container spacing={3}>
        {/* Trips by Status Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Trips by Status
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={tripsByStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {tripsByStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Trips by Route Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Top Routes by Trip Count
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={tripsByRouteData}
                layout="vertical"
                margin={{
                  top: 5,
                  right: 30,
                  left: 100,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" name="Trips" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Occupancy Rate Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Top Routes by Occupancy Rate
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={occupancyRateData}
                layout="vertical"
                margin={{
                  top: 5,
                  right: 30,
                  left: 100,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis type="category" dataKey="name" />
                <Tooltip formatter={(value) => [`${value.toFixed(1)}%`, 'Occupancy Rate']} />
                <Legend />
                <Bar dataKey="value" name="Occupancy Rate (%)" fill="#00C49F" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Revenue by Route Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 300,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Top Routes by Revenue
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={revenueByRouteData}
                layout="vertical"
                margin={{
                  top: 5,
                  right: 30,
                  left: 100,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" />
                <Tooltip formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']} />
                <Legend />
                <Bar dataKey="value" name="Revenue ($)" fill="#FFBB28" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Trips Table */}
      <Paper sx={{ width: '100%', mt: 3 }}>
        <Typography variant="h6" sx={{ p: 2 }}>
          Recent Trips
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Trip ID</TableCell>
                <TableCell>Route</TableCell>
                <TableCell>Departure</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Occupancy</TableCell>
                <TableCell>Revenue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trips
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((trip) => {
                  const occupancyRate = trip.totalSeats 
                    ? ((trip.totalSeats - trip.availableSeats) / trip.totalSeats * 100).toFixed(1) 
                    : 'N/A';
                  
                  return (
                    <TableRow key={trip.id}>
                      <TableCell>{trip.id.substring(0, 8)}</TableCell>
                      <TableCell>{`${trip.origin} - ${trip.destination}`}</TableCell>
                      <TableCell>
                        {trip.departureTime 
                          ? new Date(trip.departureTime.seconds * 1000).toLocaleString() 
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={trip.status} 
                          color={STATUS_COLORS[trip.status] || 'default'} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>{`${occupancyRate}%`}</TableCell>
                      <TableCell>${trip.revenue || 0}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={trips.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Container>
  );
};

export default TripsDashboard;
